/**
 * Account sync for the planner's local settings. These used to live only in
 * localStorage (per-browser / per-origin), so the wizard profile etc. didn't
 * follow the user across browsers, devices, or www-vs-apex. This mirrors them to
 * Firestore (users/{uid}.settings, see FIREBASE/userSettings):
 *   1. On login, HYDRATE localStorage from the cloud (cloud wins when present).
 *   2. Then patch localStorage.setItem so any change to a synced key pushes a
 *      debounced snapshot back up — no need to touch each mutation site.
 *   3. An initial push uploads any local-only values (migration / first run).
 */
// Firestore (userSettings → firebase) is imported lazily inside the sync calls so
// importing this module's pure helpers doesn't pull in / initialize Firebase.

export const SYNCED_KEYS = [
  "planner-profile",          // wizard profile
  "interval-bank-favorites",  // starred bank sessions
  "interval-bank-custom",     // custom interval sessions
  "ironman-results",          // IronMan per-event current/target
  "plan-action-overrides",    // pinned keep/remove publish actions
  "race-distance-km",         // per-race distance overrides
  "race-goaltime-overrides",  // per-race goal time
  "race-trail-overrides",     // per-race trail flag
  "race-priority-overrides",  // per-race A/B/C priority
  "alpine-peak-window",       // chosen alpine peak window
  "plan-week-meta",           // per-week plan targets (phase/hours/hard%) shown on the dashboard
  "saved-exercises",          // calendar quick-add workout library
];

/** Read the synced keys present in localStorage into a key→string snapshot. */
export function readLocalSnapshot(): Record<string, string> {
  const snap: Record<string, string> = {};
  for (const k of SYNCED_KEYS) {
    const v = localStorage.getItem(k);
    if (v != null) snap[k] = v;
  }
  return snap;
}

/** Write a cloud snapshot into localStorage (cloud wins for keys it contains). */
export function applyCloudSnapshot(snap: Record<string, string> | null): void {
  if (!snap) return;
  for (const k of SYNCED_KEYS) {
    if (typeof snap[k] === "string") localStorage.setItem(k, snap[k]);
  }
}

let patched = false;
let hydrating = false;
let currentUid: string | null = null;
let pushTimer: ReturnType<typeof setTimeout> | null = null;

function schedulePush(): void {
  if (!currentUid) return;
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(() => { void flushCloudSync(); }, 800);
}

/** Push the current local snapshot to the cloud immediately (await-able). Use
 *  before a hard reload (e.g. Reset) so a removal isn't restored on hydrate. */
export async function flushCloudSync(): Promise<void> {
  if (pushTimer) { clearTimeout(pushTimer); pushTimer = null; }
  if (!currentUid) return;
  try {
    const { saveUserSettings } = await import("../FIREBASE/userSettings");
    await saveUserSettings(currentUid, readLocalSnapshot());
  } catch { /* ignore */ }
}

function patchSetItem(): void {
  if (patched) return;
  patched = true;
  const orig = localStorage.setItem.bind(localStorage);
  localStorage.setItem = (key: string, value: string) => {
    orig(key, value);
    if (!hydrating && currentUid && SYNCED_KEYS.includes(key)) schedulePush();
  };
}

/**
 * Hydrate from the cloud, then keep localStorage mirrored up. Call once after the
 * user is authenticated, BEFORE reading the profile so it sees the synced value.
 */
export async function initCloudSync(uid: string): Promise<void> {
  currentUid = uid;
  try {
    const { loadUserSettings } = await import("../FIREBASE/userSettings");
    const cloud = await loadUserSettings(uid);
    hydrating = true;
    applyCloudSnapshot(cloud);
    hydrating = false;
  } catch {
    hydrating = false;
  }
  patchSetItem();
  // Upload any local-only values (first run / pre-sync data) and establish the doc.
  schedulePush();
}
